import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isArray, isString } from "lodash";

import type { RootState } from "@/state/types";

export const SEVERITY = {
  ERROR: "error",
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
} as const;

type SeverityValue = (typeof SEVERITY)[keyof typeof SEVERITY];

const DEFAULT_AUTO_HIDE_DURATION = 3000; // in milliseconds

export const INLINE_EDIT_AUTO_HIDE_DURATION = 1500; // in milliseconds

type Notification = {
  id: number;
  severity: SeverityValue;
  autoHide?: number | null; // null to disable auto hide
  title?: string;
  message?: string;
  inAppNotification?: boolean;
  dedupeKey?: string;
  [key: string]: unknown;
};

type NotificationPayload = {
  severity?: SeverityValue;
  autoHide?: number | null;
  title?: string;
  message?: string;
  inAppNotification?: boolean;
  /**
   * Optional identifier used to collapse repeated notifications. When a
   * notification with the same `dedupeKey` is already queued, it is replaced
   * in place (preserving its queue position) instead of appending a new one.
   * This prevents long trails of identical toasts (e.g. "Preference Saved!")
   * after rapid bursts of user actions.
   */
  dedupeKey?: string;
  [key: string]: unknown;
};

type AccessDeniedPayload = {
  message?: string;
};

type UserErrorPayload = {
  title?: string;
  message?: string;
};

type NotificationState = {
  notifications: Notification[];
};

const initialState: NotificationState = {
  notifications: [],
};

export const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (
      state,
      { payload: notification }: PayloadAction<NotificationPayload>
    ) => {
      const newNotification: Notification = {
        id: Date.now(),
        severity: SEVERITY.SUCCESS,
        autoHide: DEFAULT_AUTO_HIDE_DURATION,
        ...notification,
      };

      // If a notification with the same dedupeKey is already queued, replace
      // it in place to keep the queue short and avoid spamming the user with
      // identical toasts after rapid bursts of actions.
      if (newNotification.dedupeKey) {
        const existingIndex = state.notifications.findIndex(
          (n) => n.dedupeKey === newNotification.dedupeKey
        );
        if (
          existingIndex !== -1 &&
          state.notifications[existingIndex] !== undefined
        ) {
          // Preserve the existing id so the rendered Snackbar (keyed by id)
          // does not flicker/remount when the head of the queue is replaced.
          const existingId = state.notifications[existingIndex].id;
          const next = [...state.notifications];
          next[existingIndex] = { ...newNotification, id: existingId };
          state.notifications = next;
          return;
        }
      }

      state.notifications = [...state.notifications, newNotification];
    },
    removeNotification: (state) => {
      const [, ...notifications] = state.notifications;
      state.notifications = notifications;
    },
    // Pure (A13): Sentry hears of it from `state/notificationReporting`'s listener.
    serverErrorNotification: (state, { payload }: PayloadAction<unknown>) => {
      const message =
        payload instanceof Error
          ? payload.message
          : isRecord(payload)
            ? payload.message
            : undefined;

      state.notifications = [
        ...state.notifications,
        {
          id: Date.now(),
          severity: SEVERITY.ERROR,
          title: "An error occured",
          message: formatErrorMessage(message),
        },
      ];
    },
    accessDeniedNotification: (
      state,
      { payload }: PayloadAction<AccessDeniedPayload>
    ) => {
      state.notifications = [
        ...state.notifications,
        {
          id: Date.now(),
          severity: SEVERITY.ERROR,
          title: "Access Denied",
          message:
            payload?.message ?? "You are not authorized to perform this action",
        },
      ];
    },
    userErrorNotification: (
      state,
      { payload }: PayloadAction<UserErrorPayload>
    ) => {
      state.notifications = [
        ...state.notifications,
        {
          id: Date.now(),
          severity: SEVERITY.ERROR,
          title: formatErrorMessage(payload?.title || "Incorrect input"),
          message: formatErrorMessage(
            payload?.message || "Your provided input value is invalid"
          ),
        },
      ];
    },
  },
});

export const {
  addNotification,
  removeNotification,
  serverErrorNotification,
  accessDeniedNotification,
  userErrorNotification,
} = notificationSlice.actions;

export const selectNotification = (state: RootState): Notification | null =>
  state.notifications.notifications[0] ?? null;

export default notificationSlice.reducer;

function formatErrorMessage(message: unknown): string {
  if (isString(message)) {
    return `We're working on it. Please try refreshing or contact support with the following error message.\n${capitalizeFirstLetterOfSentences(
      message
    )}`;
  }
  if (isArray(message)) {
    return capitalizeFirstLetterOfSentences(
      (message as string[]).map((item) => ensureEndsWithPeriod(item)).join(" ")
    );
  }
  return "An unexpected error occurred, we're on it.";
}

function ensureEndsWithPeriod(sentence: string): string {
  return sentence.trim().endsWith(".") ? sentence : `${sentence}.`;
}

function capitalizeFirstLetterOfSentences(text: string): string {
  return text
    .split(/(?<=\.)\s+/)
    .map((sentence) => sentence.charAt(0).toUpperCase() + sentence.slice(1))
    .join(" ");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
